import { Component, EventEmitter, Inject, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { EditOrganizationDiscountCheckRequest } from '../../../../../models/orderModel';
import { BackOfficeService } from '../../../../../services/backoffice.service';
import { Subject, takeUntil } from 'rxjs';
import { MyConfigOrganization } from '../../../../../models/myConfig';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-discount-form',
  templateUrl: './discount-form.component.html',
  styleUrl: './discount-form.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class DiscountFormComponent {
  // @Input() pricing: any;
  // @Input() organization: MyConfigOrganization;
  @Output() saveChangesEvent = new EventEmitter<any>();
  isLoading: boolean = false;
  pricingForm: UntypedFormGroup;
  componentDestroyed$: Subject<boolean> = new Subject();
  // parkingVMs: ParkingVM[] = [];
  activeRequests: number = 0;
  discountCheckTypes = [
    { value: 1, name: 'PARKING' },
    { value: 2, name: 'ADDITIONALSERVICES' },
    { value: 4, name: 'SUBSCRIPTION' },
    { value: 8, name: 'DRIVEIN' },
  ];

  // Quotas are validated against the code's validity period, so it is locked while quotas use the code
  get quotaParkingNames(): string[] {
    return this.data?.pricing?.quotaParkingNames ?? [];
  }

  get usedInQuotas(): boolean {
    return this.quotaParkingNames.length > 0;
  }

  get discountCheckTypesArray() {
    return this.pricingForm.get('discountCheckTypes') as FormArray;
  }

  constructor(
    private fb: UntypedFormBuilder,
    private BackOfficeService: BackOfficeService,
    // private parkingService: ParkingService,
    public snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: { pricing: any, organization: MyConfigOrganization, edit: boolean },
    @Inject(MatDialogRef<DiscountFormComponent>) private dialogRef: MatDialogRef<DiscountFormComponent>
  ) {
    this.initEmptyPricingForm();
  }

  isFormInitialized = false;

  ngOnInit(): void {
    if (this.data.pricing) {
      this.initPricingForm();
    } else {
      this.initEmptyPricingForm();
    }

    this.initializeCheckboxes();
    this.isFormInitialized = true;
  }

  setSwedishTimeZone(controlName: string, event: any) {
    const selectedDate = new Date(event.value);
    if (selectedDate) {
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;
      this.pricingForm.get(controlName)?.setValue(dateString);
    } else {
      this.pricingForm.get(controlName)?.setValue(null);
    }
  }

  private setLoadingState(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  private incrementRequests() {
    this.activeRequests++;
    this.setLoadingState(true);
  }

  private decrementRequests() {
    if (this.activeRequests > 0)
      this.activeRequests--;
    if (this.activeRequests === 0) {
      this.setLoadingState(false);
    }
  }

  submitForm() {
    this.incrementRequests();

    var model: EditOrganizationDiscountCheckRequest = this.createPricingPayload();

    this.BackOfficeService
      .saveOrganizationDiscountCheck(this.data.organization.organizationId, model)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (discountCheck) => {
          this.snackBar.open('Discount check created: ' + discountCheck.id, 'Success', {
            verticalPosition: 'top',
            duration: 2000
          });
          this.decrementRequests();
          this.saveChangesEvent.emit(discountCheck);
          this.dialogRef.close();
        },
        error: (err) => {
          this.snackBar.open('Failed to create discount check: ' + err.message, 'Error', {
            verticalPosition: 'top',
            duration: 2000
          });
          this.decrementRequests();
        }
      });
  }

  createPricingPayload() {
    return {
      id: this.pricingForm.controls['id'].value !== '' ? this.pricingForm.controls['id'].value : null,
      parkingId: this.pricingForm.controls['parkingId'].value !== '' ? this.pricingForm.controls['parkingId'].value : null,
      discountCode: this.pricingForm.controls['discountCode'].value,
      discountPercent: this.pricingForm.controls['discountPercent'].value !== '' ? this.pricingForm.controls['discountPercent'].value : null,
      discountAmount: this.pricingForm.controls['discountAmount'].value !== '' ? this.pricingForm.controls['discountAmount'].value : null,
      validFrom: this.pricingForm.controls['validFrom'].value !== '' ? this.pricingForm.controls['validFrom'].value : null,
      validTo: this.pricingForm.controls['validTo'].value !== '' ? this.pricingForm.controls['validTo'].value : null,
      discountType: this.getSelectedTypesSum()
    };
  }

  initPricingForm() {
    this.pricingForm = this.fb.group({
      id: [{ value: this.data.pricing.id || '', disabled: (this.data.edit || this.isLoading) }],
      parkingId: [{ value: this.data.pricing.parkingId || '', disabled: (this.data.edit || this.isLoading) }],
      discountType: [{
        value: this.data.pricing.discountPercent ? "discountPercent" : "discountAmount",
        disabled: (this.data.edit || this.isLoading)
      }, Validators.required],
      discountCode: [{
        value: this.data.pricing.discountCode || '',
        disabled: (this.data.edit || this.isLoading)
      }, Validators.required],
      discountPercent: [{ value: this.data.pricing.discountPercent, disabled: (this.data.edit || this.isLoading) }],
      discountCheckTypes: this.fb.array([], [Validators.required]),
      discountAmount: [{ value: this.data.pricing.discountAmount, disabled: (this.data.edit || this.isLoading) }],
      validFrom: [{ value: this.data.pricing.validFrom, disabled: (this.isLoading || this.usedInQuotas) }],
      validTo: [{ value: this.data.pricing.validTo, disabled: (this.isLoading || this.usedInQuotas) }]
    }, {
      validators: (formGroup) => {
        const discountPercentControl = formGroup.get('discountPercent');
        const discountAmountControl = formGroup.get('discountAmount');

        const discountPercent = discountPercentControl?.value;
        const discountAmount = discountAmountControl?.value;

        discountPercentControl?.setErrors(null);
        discountAmountControl?.setErrors(null);

        if ((discountPercent && discountAmount) || (!discountPercent && !discountAmount)) {
          if (discountPercentControl) {
            discountPercentControl.setErrors({ discountValidationError: true });
          }
          if (discountAmountControl) {
            discountAmountControl.setErrors({ discountValidationError: true });
          }
        }

        return null;
      }
    });
  }

  initEmptyPricingForm() {
    this.pricingForm = this.fb.group({
      id: [{ value: '', disabled: this.isLoading }],
      parkingId: [{ value: '', disabled: this.isLoading }],
      discountCode: [{ value: '', disabled: this.isLoading }, Validators.required],
      discountType: [{ value: 'discountPercent', disabled: this.isLoading }, Validators.required],
      discountCheckTypes: this.fb.array([], [Validators.required]),
      discountPercent: [{ value: '', disabled: this.isLoading }],
      discountAmount: [{ value: '', disabled: this.isLoading }],
      validFrom: [{ value: '', disabled: this.isLoading }],
      validTo: [{ value: '', disabled: this.isLoading }]
    }, {
      validators: (formGroup) => {
        const discountPercentControl = formGroup.get('discountPercent');
        const discountAmountControl = formGroup.get('discountAmount');

        const discountPercent = discountPercentControl?.value;
        const discountAmount = discountAmountControl?.value;

        discountPercentControl?.setErrors(null);
        discountAmountControl?.setErrors(null);

        if ((discountPercent && discountAmount) || (!discountPercent && !discountAmount)) {
          if (discountPercentControl) {
            discountPercentControl.setErrors({ discountValidationError: true });
          }
          if (discountAmountControl) {
            discountAmountControl.setErrors({ discountValidationError: true });
          }
        }

        return null;
      }
    });
  }


  private initializeCheckboxes() {
    if (!this.pricingForm) return;

    const discountTypesSum = this.data?.pricing?.discountType || 0;

    while (this.discountCheckTypesArray.length) {
      this.discountCheckTypesArray.removeAt(0);
    }

    this.discountCheckTypes.forEach(type => {
      const isChecked = (discountTypesSum & type.value) === type.value;
      this.discountCheckTypesArray.push(this.fb.control({ value: isChecked, disabled: (this.data.edit || this.isLoading) }));
    });
  }

  private getSelectedTypesSum(): number {
    return this.discountCheckTypesArray.controls
      .map((control, i) => control.value ? this.discountCheckTypes[i].value : 0)
      .reduce((sum, value) => sum + value, 0);
  }

  clearDate(event, value) {
    event.stopPropagation();
    value.setValue('', { emitEvent: false });
  }

}
